import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { milestonesGridSaga } from './saga';
import { MilestonesGridState } from './types';
import { MilestonesGridData, MilestoneColumnConfig } from '../types';
import {
  isSharedViewPageLoad,
  shouldSkipPreferencePersistence
} from 'utils/sharedViewMode';

// LocalStorage key for user preferences only (no cache)
const PREFERENCES_KEY = 'milestonesGrid_preferences';

// Clean up old cache from localStorage if it exists
try {
  const OLD_CACHE_KEY = 'milestonesGrid_cache';
  if (localStorage.getItem(OLD_CACHE_KEY)) {
    localStorage.removeItem(OLD_CACHE_KEY);
  }
} catch (error) {
  // Silently fail if localStorage is not available
}

/**
 * Load preferences from localStorage
 */
const loadPreferences = (): {
  name?: string[];
  sharedSort?: string;
  sharedSortColumn?: string;
  sharedPin?: string;
  sharedPinColumn?: string;
  isMetaTableCollapsed?: boolean;
} => {
  try {
    const saved = localStorage.getItem(PREFERENCES_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);

      // Ensure mutual exclusivity: if both name and shared sorts exist, clear shared
      if (parsed.name && parsed.name.length > 0 && parsed.sharedSort) {
        parsed.sharedSort = undefined;
        parsed.sharedSortColumn = undefined;
        if (!shouldSkipPreferencePersistence()) {
          localStorage.setItem(PREFERENCES_KEY, JSON.stringify(parsed));
        }
      }

      return parsed;
    }
  } catch (error) {
    // Silently fail if localStorage is not available
  }
  return {};
};

/**
 * Save preferences to localStorage
 */
const savePreferences = (preferences: {
  name: string[];
  sharedSort?: string;
  sharedSortColumn?: string;
  sharedPin?: string;
  sharedPinColumn?: string;
  isMetaTableCollapsed: boolean;
}): void => {
  if (shouldSkipPreferencePersistence()) {
    return;
  }

  try {
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences));
  } catch (error) {
    // Silently fail if localStorage is not available
  }
};

// Load persisted preferences (skip on shared-view page load)
const persistedPreferences =
  typeof window !== 'undefined' && isSharedViewPageLoad()
    ? {}
    : loadPreferences();

export const initialState: MilestonesGridState = {
  data: null,
  loading: false,
  initialLoad: true, // Always true on initial state (will show full loader on first load only)
  error: null,
  columnConfig: [],
  columnConfigLoading: false,
  sortSelections: {
    name: persistedPreferences.name || [],
    sharedSort: persistedPreferences.sharedSort,
    sharedSortColumn: persistedPreferences.sharedSortColumn,
    sharedPin: persistedPreferences.sharedPin,
    sharedPinColumn: persistedPreferences.sharedPinColumn
  },
  isMetaTableCollapsed: persistedPreferences.isMetaTableCollapsed || false
};

const slice = createSlice({
  name: 'milestonesGrid',
  initialState,
  reducers: {
    loadMilestonesData(
      state,
      action: PayloadAction<{
        startDate?: string;
        endDate?: string;
      }>
    ) {
      state.loading = true;
      state.error = null;
    },
    milestonesDataLoaded(state, action: PayloadAction<MilestonesGridData>) {
      state.data = action.payload;
      state.loading = false;
      state.initialLoad = false; // No longer initial load after first data load
      state.error = null;
    },
    loadMilestonesDataError(state, action: PayloadAction<any>) {
      state.error = action.payload;
      state.loading = false;
    },
    clearMilestonesData(state) {
      state.data = null;
      state.error = null;
    },
    loadColumnConfig(state) {
      state.columnConfigLoading = true;
    },
    columnConfigLoaded(state, action: PayloadAction<MilestoneColumnConfig[]>) {
      state.columnConfig = action.payload;
      state.columnConfigLoading = false;
    },
    setSortSelection(
      state,
      action: PayloadAction<{ columnKey: string; optionKey: string }>
    ) {
      const { columnKey, optionKey } = action.payload;

      // Handle NAME column independently
      if (columnKey === 'name') {
        const currentNameSelections = state.sortSelections.name || [];

        if (currentNameSelections.includes(optionKey)) {
          // Deselect if already selected
          state.sortSelections.name = currentNameSelections.filter(
            opt => opt !== optionKey
          );
        } else {
          // Name column: only one option at a time (no pins for name)
          state.sortSelections.name = [optionKey];
        }

        // Clear any shared column sort when name column is sorted
        state.sortSelections.sharedSort = undefined;
        state.sortSelections.sharedSortColumn = undefined;

        // Persist to localStorage
        savePreferences({
          ...state.sortSelections,
          isMetaTableCollapsed: state.isMetaTableCollapsed
        });
        return;
      }

      // Handle SHARED columns (date, tier, future columns)
      const isPin = optionKey === 'pinLeft' || optionKey === 'pinRight';

      if (isPin) {
        // Handle PIN options - only one pin active across all shared columns
        const currentPinColumn = state.sortSelections.sharedPinColumn;
        const currentPin = state.sortSelections.sharedPin;

        // If clicking the same pin on the same column, deselect it
        if (currentPinColumn === columnKey && currentPin === optionKey) {
          state.sortSelections.sharedPin = undefined;
          state.sortSelections.sharedPinColumn = undefined;
        } else {
          // Set new pin (automatically clears previous pin)
          state.sortSelections.sharedPin = optionKey;
          state.sortSelections.sharedPinColumn = columnKey;
        }
      } else {
        // Handle SORT options - only one sort active across all shared columns
        const currentSortColumn = state.sortSelections.sharedSortColumn;
        const currentSort = state.sortSelections.sharedSort;

        // If clicking the same sort on the same column, deselect it
        if (currentSortColumn === columnKey && currentSort === optionKey) {
          state.sortSelections.sharedSort = undefined;
          state.sortSelections.sharedSortColumn = undefined;
        } else {
          // Set new sort (automatically clears previous sort from other columns)
          state.sortSelections.sharedSort = optionKey;
          state.sortSelections.sharedSortColumn = columnKey;

          // Clear name column sort when a shared column sort is applied
          state.sortSelections.name = [];
        }
      }

      // Persist to localStorage (for shared columns)
      savePreferences({
        ...state.sortSelections,
        isMetaTableCollapsed: state.isMetaTableCollapsed
      });
    },
    clearSortSelections(state) {
      state.sortSelections = {
        name: [],
        sharedSort: undefined,
        sharedSortColumn: undefined,
        sharedPin: undefined,
        sharedPinColumn: undefined
      };
      // Persist to localStorage
      savePreferences({
        ...state.sortSelections,
        isMetaTableCollapsed: state.isMetaTableCollapsed
      });
    },
    toggleMetaTableCollapsed(state) {
      state.isMetaTableCollapsed = !state.isMetaTableCollapsed;
      // Persist to localStorage
      savePreferences({
        ...state.sortSelections,
        isMetaTableCollapsed: state.isMetaTableCollapsed
      });
    },
    // Apply view state from deep link URL
    applyViewStateFromUrl(
      state,
      action: PayloadAction<{
        sortColumn?: string;
        sortOption?: string;
        pinColumn?: string;
        pinOption?: string;
        isMetaTableCollapsed?: boolean;
      }>
    ) {
      const params = action.payload;

      // Apply sorting
      if (params.sortColumn && params.sortOption) {
        if (params.sortColumn === 'name') {
          state.sortSelections.name = [params.sortOption];
        } else {
          state.sortSelections.sharedSort = params.sortOption;
          state.sortSelections.sharedSortColumn = params.sortColumn;
        }
      }

      // Apply pinning
      if (params.pinColumn && params.pinOption) {
        state.sortSelections.sharedPin = params.pinOption;
        state.sortSelections.sharedPinColumn = params.pinColumn;
      }

      // Apply meta table collapsed state
      if (params.isMetaTableCollapsed !== undefined) {
        state.isMetaTableCollapsed = params.isMetaTableCollapsed;
      }

      // Save preferences
      savePreferences({
        ...state.sortSelections,
        isMetaTableCollapsed: state.isMetaTableCollapsed
      });
    }
  }
});

export const { actions: milestonesGridActions } = slice;

export const useMilestonesGridSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: milestonesGridSaga });
  return { actions: slice.actions };
};

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = useMilestonesGridSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.loadMilestonesData({ startDate: '2025-01-01', endDate: '2026-12-31' }));
 *   };
 * }
 */
